import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ChevronRight, ChevronLeft, User, Baby, Sparkles, MapPin, Plus, X, AlertTriangle, Utensils, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERESTS = [
  "Outdoor Play", "Arts & Crafts", "Sports", "Reading", "Music", "Science",
  "Nature Walks", "Board Games", "Swimming", "Cooking", "Dance", "Building/LEGO"
];

const CHILD_INTERESTS = [
  "Dinosaurs", "Princesses", "Superheroes", "Animals", "Cars & Trucks", "Dolls",
  "Drawing", "Building", "Running", "Swimming", "Music", "Books"
];

const ALLERGENS = [
  "None", "Nuts", "Peanuts", "Tree Nuts", "Dairy", "Eggs", "Gluten", "Wheat", 
  "Soy", "Fish", "Shellfish", "Sesame", "Mustard"
];

const DIETARY_PREFERENCES = [
  "Halal", "Kosher", "Vegetarian", "Vegan", "Pescatarian", 
  "Gluten-Free", "Dairy-Free", "No Pork", "No Beef"
];

const SPECIAL_NEEDS = [
  "Autism/ASD", "ADHD", "Down Syndrome", "Cerebral Palsy",
  "Hearing Impairment", "Visual Impairment", "Learning Disability",
  "Speech Delay", "Sensory Processing"
];

const MOBILITY_NEEDS = [
  "Wheelchair User", "Walking Aid", "Mobility Scooter"
];

const MEDICAL_NEEDS = [
  "PEG Feeding", "Tracheostomy", "Oxygen Required", "Epilepsy", "Diabetes"
];

const ACCESSIBILITY_REQUIREMENTS = [
  "Wheelchair Access", "Changing Places Toilet", "Sensory Friendly Venues",
  "Quiet Spaces", "Visual Aids/PECS", "Hearing Loop"
];

const LANGUAGES = [
  "English", "Polish", "Urdu", "Punjabi", "Bengali", "Gujarati", 
  "Hindi", "Arabic", "French", "Spanish", "Portuguese", "Mandarin",
  "Cantonese", "Somali", "Romanian", "Turkish", "Italian", "Tamil",
  "Ashanti", "Yoruba", "Zulu", "Sotho", "Swahili"
];

const RADIUS_OPTIONS = [
  { value: 5, label: "5 miles - Very local" },
  { value: 10, label: "10 miles - My village" },
  { value: 15, label: "15 miles - Wider area" },
  { value: 25, label: "25 miles - City-wide" },
];

const Onboarding = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Step 1: Parent info
  const [parentName, setParentName] = useState(user?.name || "");
  const [bio, setBio] = useState("");
  const [locationCity, setLocationCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [searchRadius, setSearchRadius] = useState(10);
  const [homeLanguages, setHomeLanguages] = useState(["English"]);
  const [preferredLanguage, setPreferredLanguage] = useState("English");

  // Step 2: Children (with special needs)
  const [children, setChildren] = useState([{ 
    name: "", 
    age: "", 
    interests: [], 
    allergens: [],
    special_needs: [],
    uses_sunflower_lanyard: false,
    requires_carer: false,
    mobility_needs: [],
    medical_needs: [],
    additional_support_notes: ""
  }]);

  // Step 3: Allergens & Dietary
  const [familyAllergens, setFamilyAllergens] = useState([]);
  const [dietaryPreferences, setDietaryPreferences] = useState([]);

  // Step 4: Interests
  const [selectedInterests, setSelectedInterests] = useState([]);

  // Step 5: Accessibility requirements
  const [accessibilityRequirements, setAccessibilityRequirements] = useState([]);

  const addChild = () => {
    setChildren([...children, { 
      name: "", 
      age: "", 
      interests: [], 
      allergens: [],
      special_needs: [],
      uses_sunflower_lanyard: false,
      requires_carer: false,
      mobility_needs: [],
      medical_needs: [],
      additional_support_notes: ""
    }]);
  };

  const removeChild = (index) => {
    if (children.length > 1) {
      setChildren(children.filter((_, i) => i !== index));
    }
  };

  const updateChild = (index, field, value) => {
    const updated = [...children];
    updated[index][field] = value;
    setChildren(updated);
  };

  const toggleChildInterest = (childIndex, interest) => {
    const updated = [...children];
    const interests = updated[childIndex].interests;
    if (interests.includes(interest)) {
      updated[childIndex].interests = interests.filter(i => i !== interest);
    } else if (interests.length < 5) {
      updated[childIndex].interests = [...interests, interest];
    }
    setChildren(updated);
  };

  const toggleChildAllergen = (childIndex, allergen) => {
    const updated = [...children];
    const allergens = updated[childIndex].allergens || [];
    if (allergens.includes(allergen)) {
      updated[childIndex].allergens = allergens.filter(a => a !== allergen);
    } else {
      updated[childIndex].allergens = [...allergens, allergen];
    }
    setChildren(updated);
  };

  const toggleFamilyAllergen = (allergen) => {
    if (familyAllergens.includes(allergen)) {
      setFamilyAllergens(familyAllergens.filter(a => a !== allergen));
    } else {
      setFamilyAllergens([...familyAllergens, allergen]);
    }
  };

  const toggleDietaryPreference = (pref) => {
    if (dietaryPreferences.includes(pref)) {
      setDietaryPreferences(dietaryPreferences.filter(p => p !== pref));
    } else {
      setDietaryPreferences([...dietaryPreferences, pref]);
    }
  };

  const toggleLanguage = (lang) => {
    if (homeLanguages.includes(lang)) {
      // Don't remove if it's the only language
      if (homeLanguages.length > 1) {
        setHomeLanguages(homeLanguages.filter(l => l !== lang));
        // If removing the preferred language, set to first remaining
        if (preferredLanguage === lang) {
          setPreferredLanguage(homeLanguages.filter(l => l !== lang)[0]);
        }
      }
    } else {
      setHomeLanguages([...homeLanguages, lang]);
    }
  };

  const toggleInterest = (interest) => {
    if (selectedInterests.includes(interest)) {
      setSelectedInterests(selectedInterests.filter(i => i !== interest));
    } else if (selectedInterests.length < 6) {
      setSelectedInterests([...selectedInterests, interest]);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const profileData = {
        parent_name: parentName,
        bio,
        location_city: locationCity,
        postcode: postcode.toUpperCase().trim(),
        search_radius: searchRadius,
        children: children.filter(c => c.name && c.age).map(c => ({
          name: c.name,
          age: parseInt(c.age),
          interests: c.interests,
          allergens: c.allergens || [],
          special_needs: c.special_needs || [],
          uses_sunflower_lanyard: c.uses_sunflower_lanyard || false,
          requires_carer: c.requires_carer || false,
          mobility_needs: c.mobility_needs || [],
          medical_needs: c.medical_needs || [],
          additional_support_notes: c.additional_support_notes || ""
        })),
        interests: selectedInterests,
        allergens: familyAllergens,
        dietary_preferences: dietaryPreferences,
        accessibility_requirements: accessibilityRequirements,
        home_languages: homeLanguages,
        preferred_language: preferredLanguage
      };

      await axios.post(`${API}/profile`, profileData);
      await refreshUser();
      toast.success("Profile created! Let's find your family's playmates!");
      navigate("/discover");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create profile");
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return parentName && locationCity && postcode;
    if (step === 2) return children.some(c => c.name && c.age);
    if (step === 3) return true; // Allergens/dietary are optional
    if (step === 4) return selectedInterests.length >= 2;
    if (step === 5) return true; // Accessibility is optional
    return false;
  };

  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      {/* Decorative blobs */}
      <div className="blob blob-pink w-80 h-80 top-20 -right-40 fixed" />
      <div className="blob blob-blue w-64 h-64 bottom-40 -left-32 fixed" />
      <div className="blob blob-yellow w-48 h-48 top-1/3 left-1/4 fixed" />

      <div className="relative z-10 max-w-2xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full hero-gradient flex items-center justify-center">
            <Heart className="w-6 h-6 text-white" fill="white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Welcome to KiddiPlay!
            </h1>
            <p className="text-slate-600">Let's set up your family profile</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                  s < step ? 'bg-teal-500 text-white' :
                  s === step ? 'bg-teal-500 text-white ring-4 ring-teal-200' :
                  'bg-slate-200 text-slate-500'
                }`}>
                  {s < step ? '✓' : s}
                </div>
                {s < 4 && <div className={`w-16 sm:w-24 h-1 mx-2 rounded ${s < step ? 'bg-teal-500' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs sm:text-sm text-slate-600">
            <span>About You</span>
            <span>Your Kids</span>
            <span>Safety Info</span>
            <span>Interests</span>
          </div>
        </div>

        {/* Step content */}
        <div className="bg-white rounded-3xl shadow-lg p-8 animate-fade-in">
          {/* Step 1: Parent Info */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                  <User className="w-6 h-6 text-teal-500" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>About You</h2>
                  <p className="text-slate-500">Tell other families about yourself</p>
                </div>
              </div>

              <div>
                <Label htmlFor="parentName">Your Name</Label>
                <Input
                  id="parentName"
                  value={parentName}
                  onChange={(e) => setParentName(e.target.value)}
                  placeholder="Sarah Johnson"
                  className="mt-1 h-12 rounded-xl"
                  data-testid="parent-name-input"
                />
              </div>

              <div>
                <Label htmlFor="location">City / Neighborhood</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="location"
                    value={locationCity}
                    onChange={(e) => setLocationCity(e.target.value)}
                    placeholder="Manchester, UK"
                    className="pl-12 h-12 rounded-xl"
                    data-testid="location-input"
                  />
                </div>
              </div>

              {/* Where's Your Village - Postcode & Radius */}
              <div className="p-5 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl border border-teal-200">
                <Label className="flex items-center gap-2 mb-2 text-teal-800 font-semibold text-lg">
                  <MapPin className="w-5 h-5" />
                  Where's your village?
                </Label>
                <p className="text-sm text-teal-600 mb-4">
                  We'll only show you families nearby - so you can actually meet up!
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postcode" className="text-teal-700">Your Postcode</Label>
                    <Input
                      id="postcode"
                      value={postcode}
                      onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                      placeholder="M1 4BT"
                      className="mt-1 h-12 rounded-xl uppercase"
                      maxLength={8}
                      data-testid="postcode-input"
                    />
                    <p className="text-xs text-teal-500 mt-1">Used for distance matching only</p>
                  </div>
                  <div>
                    <Label htmlFor="radius" className="text-teal-700">Search Radius</Label>
                    <select
                      id="radius"
                      value={searchRadius}
                      onChange={(e) => setSearchRadius(parseInt(e.target.value))}
                      className="w-full mt-1 h-12 px-4 rounded-xl border border-teal-200 focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                      data-testid="radius-select"
                    >
                      {RADIUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="bio">A bit about your family (optional)</Label>
                <Textarea
                  id="bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="We're an active family who loves weekend adventures..."
                  className="mt-1 rounded-xl min-h-[100px]"
                  data-testid="bio-input"
                />
              </div>

              {/* Languages Section */}
              <div className="p-5 bg-purple-50 rounded-2xl border border-purple-200">
                <Label className="flex items-center gap-2 mb-3 text-purple-800 font-semibold">
                  <Globe className="w-5 h-5" />
                  Languages Spoken at Home
                </Label>
                <p className="text-sm text-purple-600 mb-4">
                  Helps connect you with families who speak the same languages - great for expats!
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => toggleLanguage(lang)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        homeLanguages.includes(lang)
                          ? 'bg-purple-500 text-white'
                          : 'bg-white text-slate-600 border border-purple-200 hover:border-purple-400'
                      }`}
                      data-testid={`language-${lang}`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
                
                {homeLanguages.length > 1 && (
                  <div className="mt-4 pt-4 border-t border-purple-200">
                    <Label className="text-sm text-purple-700 mb-2 block">Preferred language for messaging:</Label>
                    <select
                      value={preferredLanguage}
                      onChange={(e) => setPreferredLanguage(e.target.value)}
                      className="w-full px-4 py-2 rounded-xl border border-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                      data-testid="preferred-language-select"
                    >
                      {homeLanguages.map((lang) => (
                        <option key={lang} value={lang}>{lang}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Children */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center">
                  <Baby className="w-6 h-6 text-pink-500" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>Your Kids</h2>
                  <p className="text-slate-500">Help us find age-appropriate playmates</p>
                </div>
              </div>

              {children.map((child, index) => (
                <div key={index} className="p-6 bg-slate-50 rounded-2xl relative">
                  {children.length > 1 && (
                    <button
                      onClick={() => removeChild(index)}
                      className="absolute top-4 right-4 text-slate-400 hover:text-red-500"
                      data-testid={`remove-child-${index}`}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label>Child's Name</Label>
                      <Input
                        value={child.name}
                        onChange={(e) => updateChild(index, 'name', e.target.value)}
                        placeholder="Emma"
                        className="mt-1 h-12 rounded-xl"
                        data-testid={`child-name-${index}`}
                      />
                    </div>
                    <div>
                      <Label>Age</Label>
                      <Input
                        type="number"
                        min="0"
                        max="10"
                        value={child.age}
                        onChange={(e) => updateChild(index, 'age', e.target.value)}
                        placeholder="5"
                        className="mt-1 h-12 rounded-xl"
                        data-testid={`child-age-${index}`}
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <Label className="block mb-2">Interests (pick up to 5)</Label>
                    <div className="flex flex-wrap gap-2">
                      {CHILD_INTERESTS.map((interest) => (
                        <button
                          key={interest}
                          type="button"
                          onClick={() => toggleChildInterest(index, interest)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            child.interests.includes(interest)
                              ? 'bg-pink-500 text-white'
                              : 'bg-white text-slate-600 border border-slate-200 hover:border-pink-300'
                          }`}
                          data-testid={`child-interest-${index}-${interest}`}
                        >
                          {interest}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="block mb-2 text-amber-700">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      Allergens (for this child)
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {ALLERGENS.slice(0, 8).map((allergen) => (
                        <button
                          key={allergen}
                          type="button"
                          onClick={() => toggleChildAllergen(index, allergen)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            (child.allergens || []).includes(allergen)
                              ? 'bg-amber-500 text-white'
                              : 'bg-white text-slate-600 border border-amber-200 hover:border-amber-400'
                          }`}
                          data-testid={`child-allergen-${index}-${allergen}`}
                        >
                          {allergen}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              <Button
                type="button"
                variant="outline"
                onClick={addChild}
                className="w-full rounded-xl py-6 border-dashed border-2"
                data-testid="add-child-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Another Child
              </Button>
            </div>
          )}

          {/* Step 3: Allergens & Dietary Preferences */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Utensils className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>Safety & Dietary Info</h2>
                  <p className="text-slate-500">Important for food-centered playdates</p>
                </div>
              </div>

              {/* Family-wide allergens */}
              <div className="p-5 bg-amber-50 rounded-2xl border border-amber-200">
                <Label className="block mb-3 text-amber-800 font-semibold">
                  <AlertTriangle className="w-5 h-5 inline mr-2" />
                  Family Allergens - Please don't bring these!
                </Label>
                <p className="text-sm text-amber-700 mb-4">
                  Other families will see this when planning food activities together.
                </p>
                <div className="flex flex-wrap gap-2">
                  {ALLERGENS.map((allergen) => (
                    <button
                      key={allergen}
                      type="button"
                      onClick={() => toggleFamilyAllergen(allergen)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        familyAllergens.includes(allergen)
                          ? 'bg-amber-500 text-white shadow-md'
                          : 'bg-white text-slate-600 border border-amber-200 hover:border-amber-400'
                      }`}
                      data-testid={`family-allergen-${allergen}`}
                    >
                      {allergen}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dietary preferences */}
              <div className="p-5 bg-green-50 rounded-2xl border border-green-200">
                <Label className="block mb-3 text-green-800 font-semibold">
                  <Utensils className="w-5 h-5 inline mr-2" />
                  Dietary Preferences
                </Label>
                <p className="text-sm text-green-700 mb-4">
                  Let families know about your food choices for meetups.
                </p>
                <div className="flex flex-wrap gap-2">
                  {DIETARY_PREFERENCES.map((pref) => (
                    <button
                      key={pref}
                      type="button"
                      onClick={() => toggleDietaryPreference(pref)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        dietaryPreferences.includes(pref)
                          ? 'bg-green-500 text-white shadow-md'
                          : 'bg-white text-slate-600 border border-green-200 hover:border-green-400'
                      }`}
                      data-testid={`dietary-${pref}`}
                    >
                      {pref}
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-center text-sm text-slate-500">
                This information is optional but highly recommended for safety!
              </p>
            </div>
          )}

          {/* Step 4: Interests */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>Family Interests</h2>
                  <p className="text-slate-500">What does your family enjoy? (pick 2-6)</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                {INTERESTS.map((interest) => (
                  <button
                    key={interest}
                    type="button"
                    onClick={() => toggleInterest(interest)}
                    className={`px-5 py-3 rounded-full text-sm font-medium transition-all ${
                      selectedInterests.includes(interest)
                        ? 'bg-teal-500 text-white shadow-lg'
                        : 'bg-white text-slate-600 border-2 border-slate-200 hover:border-teal-300'
                    }`}
                    data-testid={`interest-${interest}`}
                  >
                    {interest}
                  </button>
                ))}
              </div>

              <p className="text-sm text-slate-500 text-center">
                {selectedInterests.length}/6 selected
              </p>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
            {step > 1 ? (
              <Button
                variant="ghost"
                onClick={() => setStep(step - 1)}
                className="text-slate-600"
                data-testid="back-btn"
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {step < 4 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-8"
                data-testid="next-btn"
              >
                Next
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!canProceed() || loading}
                className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-8"
                data-testid="complete-btn"
              >
                {loading ? "Creating..." : "Let's Go!"}
                <Sparkles className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
